import React, { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import * as Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import moment from "moment";
import useAuth from "../../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { Label, Select } from "flowbite-react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import CountryOptions from "../../../public/CountryOptions";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import { useAddUserManually } from "../../../../hooks/users";
import { CustomPhoneNumber } from "../../../atoms/CustomPhoneNumber";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

type User = {
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  user_birth_date: string;
  user_country_code: string;
  user_user_name: string;
  user_type: string;
  user_role: string;
  user_phone: string;
};

const schema = Joi.object({
  users: Joi.array()
    .unique("user_email")
    .items({
      user_email: Joi.string().trim().required().label("Email"),
      user_first_name: Joi.string()
        .trim()
        .min(3)
        .max(30)
        .optional()
        .allow(null, "")
        .label("First name"),

      user_last_name: Joi.string()
        .trim()
        .min(3)
        .max(150)
        .optional()
        .allow(null, "")
        .label("Last name"),
      user_user_name: Joi.string()
        .trim()
        .min(3)
        .max(30)
        .optional()
        .allow(null, "")
        .label("Username"),
      user_role: Joi.string()
        .trim()
        .min(3)
        .max(30)
        .optional()
        .allow(null, "")
        .label("Role"),
      user_type: Joi.string()
        .trim()
        .min(3)
        .max(30)
        .optional()
        .allow(null, "")
        .label("Type"),
      user_birth_date: Joi.date()
        .optional()
        .allow(null, "")
        .label("Birth date"),
      user_country_code: Joi.string()
        .trim()
        .optional()
        .allow(null, "")
        .label("Country code"),
      user_phone: Joi.string().trim().optional().allow(null, "").label("Phone"),
    })
    .min(1)
    .required(),
});

const AddUserManuallyModal = ({
  show,
  onHide,
  size,
  className,
  onBackClick,
}: {
  show: boolean;
  onHide: () => void;
  size?: "lg" | "sm" | "xl" | "2xl";
  className?: string | undefined;
  onBackClick: () => void;
}) => {
  const [t] = useTranslation();
  const { customer_identification, user } = useAuth();
  const permissions = usePermissions(user);
  const canImportUsers = permissions.can(Cap.IMPORT_USERS, Level.WRITE);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    setError,
    clearErrors,
    watch,
  } = useForm<{ users: User[] }>({
    defaultValues: {
      users: [
        {
          user_email: "",
          user_first_name: "",
          user_last_name: "",
          user_birth_date: "",
          user_country_code: "",
          user_user_name: "",
          user_type: "",
          user_role: "",
          user_phone: "",
        },
      ],
    },
    resolver: joiResolver(schema),
  });

  const { fields, remove } = useFieldArray({
    name: "users",
    control,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [collapses, setCollapses] = useState<{ [key: string]: boolean }>({});

  const { mutate: addUsersManually } = useAddUserManually();

  const handleCloseModal = () => {
    reset();
    onHide();
  };

  const onSubmit = (data: { users: User[] }) => {
    let copyData = data;

    copyData.users = copyData.users.map((user) => {
      return {
        ...user,
        user_birth_date: user.user_birth_date
          ? moment(user.user_birth_date).format("YYYY-MM-DD")
          : "",
      };
    });

    setIsLoading(true);

    addUsersManually(
      {
        customer_identification: customer_identification,
        entityIdentification: user.entity_identification,
        data: copyData.users[0],
      },
      {
        onSuccess: () => {
          setIsLoading(false);
          handleCloseModal();
        },
        onError: (err) => {
          setIsLoading(false);
          if (err.response && err.response.data.element) {
            const errPayload: User = err.response.data.element;

            !!errPayload.user_email &&
              setError(`users.${0}.user_email`, {
                type: "required",
                message: errPayload.user_email,
              });
            !!errPayload.user_birth_date &&
              setError(`users.${0}.user_birth_date`, {
                type: "required",
                message: errPayload.user_birth_date,
              });
            !!errPayload.user_country_code &&
              setError(`users.${0}.user_country_code`, {
                type: "required",
                message: errPayload.user_country_code,
              });
            !!errPayload.user_first_name &&
              setError(`users.${0}.user_first_name`, {
                type: "required",
                message: errPayload.user_first_name,
              });
            !!errPayload.user_last_name &&
              setError(`users.${0}.user_last_name`, {
                type: "required",
                message: errPayload.user_last_name,
              });
            !!errPayload.user_role &&
              setError(`users.${0}.user_role`, {
                type: "required",
                message: errPayload.user_role,
              });
            !!errPayload.user_type &&
              setError(`users.${0}.user_type`, {
                type: "required",
                message: errPayload.user_type,
              });
            !!errPayload.user_user_name &&
              setError(`users.${0}.user_user_name`, {
                type: "required",
                message: errPayload.user_user_name,
              });
            !!errPayload.user_phone &&
              setError(`users.${0}.user_phone`, {
                type: "required",
                message: errPayload.user_phone,
              });
          }
        },
      }
    );
  };

  return (
    <ModalShell
      show={show}
      onClose={handleCloseModal}
      size={size}
      className={className}
      title={t("dashboard.addUserManually.title")}
      subtitle={
        <>
          {t("dashboard.addUserManually.text-01")} <br />
          {t("dashboard.addUserManually.text-02")}
        </>
      }
      onSubmit={handleSubmit(onSubmit)}
      footer={
        <>
          <CustomButton onClick={onHide} title={t("common.cancel") as string} />
          <CustomButton
            type="submit"
            disabled={isLoading || !canImportUsers}
            isLoading={isLoading}
            variant="primary"
            icon="userPlus"
            title={t("common.add") as string}
          />
        </>
      }
    >
      {fields.map((item, index) => (
        <div key={item.id}>
          <div className="mb-3">
            <div className="mb-3">
              <CustomInput
                key={item.id}
                name={`users.${index}.user_email`}
                control={control}
                error={
                  errors?.users &&
                  (errors?.users[index]?.user_email ||
                    (errors?.users && !!errors?.users[index]))
                }
                helperText={
                  errors?.users && errors?.users[index]?.user_email?.message
                }
                label={t("dashboard.addUserManually.field-01") as string}
                type="email"
                placeholder={
                  t("dashboard.addUserManually.placeholder-01") as string
                }
                autoComplete="email"
                onValueChange={(e) => {
                  clearErrors(`users.${index}.user_email`);

                  if (fields.find((item) => item.user_email === e.target.value))
                    setError(`users.${index}.user_email`, {
                      type: "required",
                      message: "Duplicate email",
                    });
                }}
              />
            </div>

            <div className="flex justify-center mb-3">
              <CustomButton
                variant="none"
                size="sm"
                onClick={() =>
                  setCollapses({
                    ...collapses,
                    [item.id]: !collapses[item.id],
                  })
                }
                aria-controls={`collapse_more_${item.id}`}
                aria-expanded={collapses[item.id]}
                className="rounded-md text-sm font-medium text-blue-600 enabled:hover:bg-blue-50"
                comp={
                  collapses[item.id] ? (
                    <span className="flex items-center gap-2">
                      <FaChevronUp size={14} />
                      <span>
                        {t("dashboard.addUserManually.btn-01")}{" "}
                        {t("dashboard.addUserManually.btn-01-less")}
                      </span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <FaChevronDown size={14} />
                      <span>
                        {t("dashboard.addUserManually.btn-01")}{" "}
                        {t("dashboard.addUserManually.btn-01-more")}
                      </span>
                    </span>
                  )
                }
              />
            </div>

            {collapses[item.id] && (
              <div id={`collapse_more_${item.id}`}>
                <div className="grid grid-cols-12 gap-3">
                  <div className="md:col-span-6 col-span-12">
                    <div className="mb-3">
                      <CustomInput
                        name={`users.${index}.user_first_name`}
                        control={control}
                        error={
                          errors?.users && errors?.users[index]?.user_first_name
                        }
                        helperText={
                          errors?.users &&
                          errors?.users[index]?.user_first_name?.message
                        }
                        label={
                          t("dashboard.addUserManually.field-02") as string
                        }
                        placeholder={
                          t(
                            "dashboard.addUserManually.placeholder-02"
                          ) as string
                        }
                        autoComplete="given-name"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-6 col-span-12">
                    <div className="mb-3">
                      <CustomInput
                        name={`users.${index}.user_last_name`}
                        control={control}
                        error={
                          errors?.users && errors?.users[index]?.user_last_name
                        }
                        helperText={
                          errors?.users &&
                          errors?.users[index]?.user_last_name?.message
                        }
                        label={
                          t("dashboard.addUserManually.field-03") as string
                        }
                        placeholder={
                          t(
                            "dashboard.addUserManually.placeholder-03"
                          ) as string
                        }
                        autoComplete="family-name"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-6 col-span-12">
                    <div className="mb-3">
                      <CustomInput
                        name={`users.${index}.user_user_name`}
                        control={control}
                        error={
                          errors?.users && errors?.users[index]?.user_user_name
                        }
                        helperText={
                          errors?.users &&
                          errors?.users[index]?.user_user_name?.message
                        }
                        label={
                          t("dashboard.addUserManually.field-04") as string
                        }
                        placeholder={
                          t(
                            "dashboard.addUserManually.placeholder-04"
                          ) as string
                        }
                        autoComplete="username"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-6 col-span-12">
                    <div className="mb-3">
                      <CustomInput
                        name={`users.${index}.user_birth_date`}
                        type="date"
                        control={control}
                        error={
                          errors?.users && errors?.users[index]?.user_birth_date
                        }
                        helperText={
                          errors?.users &&
                          errors?.users[index]?.user_birth_date?.message
                        }
                        label={
                          t("dashboard.addUserManually.field-08") as string
                        }
                        max={moment().format("YYYY-MM-DD")}
                        autoComplete="bday"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-6 col-span-12">
                    <div className="mb-3">
                      <Label
                        htmlFor={`user_country_code_${item.id}`}
                        value={t(
                          "dashboard.addUserManually.field-05"
                        ).toString()}
                        className="block mb-3 text-gray-500 text-xs font-normal font-figtree leading-none"
                      />

                      <Select
                        translate="no"
                        id={`user_country_code_${item.id}`}
                        autoComplete="country"
                        color={
                          errors?.users &&
                          !!errors?.users[index]?.user_country_code
                            ? "failure"
                            : "gray"
                        }
                        helperText={
                          errors?.users &&
                          errors?.users[index]?.user_country_code?.message
                        }
                        {...register(
                          `users.${index}.user_country_code` as const
                        )}
                      >
                        <option value="">
                          {t("dashboard.addUserManually.select-option")}
                        </option>
                        <CountryOptions />
                      </Select>
                    </div>
                  </div>
                  <div className="md:col-span-6 col-span-12">
                    <div className="mb-3">
                      <CustomPhoneNumber
                        name={`users.${index}.user_phone`}
                        control={control}
                        error={
                          errors?.users && errors?.users[index]?.user_phone
                        }
                        helperText={
                          errors?.users &&
                          errors?.users[index]?.user_phone?.message
                        }
                        label="Phone Number"
                        placeholder="Enter phone number"
                        autoComplete="tel"
                        defaultCountryCode={watch(
                          `users.${index}.user_country_code`
                        )}
                      />
                    </div>
                  </div>
                  <div className="md:col-span-6 col-span-12">
                    <div className="mb-3">
                      <CustomInput
                        name={`users.${index}.user_role`}
                        control={control}
                        error={errors?.users && errors?.users[index]?.user_role}
                        helperText={
                          errors?.users &&
                          errors?.users[index]?.user_role?.message
                        }
                        label={
                          t("dashboard.addUserManually.field-06") as string
                        }
                        placeholder={
                          t(
                            "dashboard.addUserManually.placeholder-06"
                          ) as string
                        }
                        autoComplete="organization-title"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-6 col-span-12">
                    <div className="mb-3">
                      <CustomInput
                        name={`users.${index}.user_type`}
                        control={control}
                        error={errors?.users && errors?.users[index]?.user_type}
                        helperText={
                          errors?.users &&
                          errors?.users[index]?.user_type?.message
                        }
                        label={
                          t("dashboard.addUserManually.field-07") as string
                        }
                        placeholder={
                          t(
                            "dashboard.addUserManually.placeholder-07"
                          ) as string
                        }
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {index !== 0 && (
              <CustomButton
                variant="danger"
                onClick={() => remove(index)}
                title={t("common.remove")}
              />
            )}
          </div>
        </div>
      ))}
    </ModalShell>
  );
};

export default AddUserManuallyModal;
